import { Dispatch, SetStateAction, useEffect } from 'react';
import { Button } from '@iwoca/orion';
import { Trans } from '@lingui/react/macro';
import {
  TDocumentTypeWithDE,
  handleDocumentTypeMapping,
} from 'components/Documents/DocumentGroup/DocumentGroupHelper';
import { useOffers } from 'components/Signup/hooks/useOffers';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from '../SubmitDocuments/SubmitDocuments.module.css';

type TProps = {
  isSubmitted: boolean;
  setIsCheckingRequirements: Dispatch<SetStateAction<boolean>>;
  setIsSubmitted: (isSubmitted: boolean) => void;
};

export const SubmitDocuments = ({
  isSubmitted,
  setIsSubmitted,
  setIsCheckingRequirements,
}: TProps) => {
  const { locale } = useLocale();

  const {
    documentRequirements,
    handleDecision,
    isDeciding,
    isRefetchingOffers,
  } = useOffers({
    getOffersEnabled: false,
  });

  const { trackEvent } = useAmplitude();

  useEffect(() => {
    if (!documentRequirements || !isSubmitted) {
      return;
    }

    const requiredDocumentType = documentRequirements.find(
      (requirement) =>
        requirement.is_optional === false &&
        (requirement.status === 'error' || requirement.status === 'required'),
    );

    const mappedDocType = handleDocumentTypeMapping(
      requiredDocumentType?.type as TDocumentTypeWithDE,
      locale,
    );

    const elementToScrollTo = document.getElementById(
      `document-requirement-${getDocumentRequirementId(mappedDocType)}`,
    );

    if (elementToScrollTo) {
      elementToScrollTo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentRequirements, isSubmitted]);

  const getDocumentRequirementId = (requirement?: string) => {
    if (!requirement) {
      return null;
    }

    if (requirement === 'company_accounts') {
      // This is because product_offers asks for "company_accounts"
      // But POST documents accepts it under "business accounts"
      return 'business accounts';
    }

    return requirement.split('_').join(' ');
  };

  return (
    <div className={styles.submitContainer}>
      <Button
        loading={isDeciding || isRefetchingOffers}
        size="large"
        onClick={async () => {
          setIsCheckingRequirements(true);
          trackEvent(AMPLITUDE_EVENTS.submitDocumentsClicked);
          await handleDecision();
          setIsSubmitted(true);
          setIsCheckingRequirements(false);
        }}
      >
        <Trans>Submit documents</Trans>
      </Button>
    </div>
  );
};
